import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

// TanStack Router's <Link> is statically typed against the route tree, but
// breadcrumb segments carry href strings assembled at runtime by each page.
// Narrow to a permissive prop shape so dynamic hrefs still get client-side
// navigation without threading route params through every segment.
const RouterLink = Link as unknown as ComponentType<{
  to: string;
  className?: string;
  children: ReactNode;
}>;

export interface BreadcrumbSegment {
  label: string;
  href?: string;
}

interface BreadcrumbContextValue {
  segments: BreadcrumbSegment[];
  setSegments: (s: BreadcrumbSegment[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  segments: [],
  setSegments: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [segments, setSegmentsState] = useState<BreadcrumbSegment[]>([]);
  const setSegments = useCallback((s: BreadcrumbSegment[]) => {
    setSegmentsState(s);
  }, []);
  const value = useMemo(() => ({ segments, setSegments }), [segments, setSegments]);

  return (
    <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
  );
}

/**
 * `leadingSeparator`: today's top bar prints a chevron before the first crumb,
 * after the wordmark. A trail standing alone (the rail frame) starts bare.
 */
export function BreadcrumbDisplay({
  leadingSeparator = true,
}: {
  leadingSeparator?: boolean;
} = {}) {
  const { segments } = useContext(BreadcrumbContext);
  if (segments.length === 0) return null;

  return (
    <nav className="flex items-center gap-2 text-sm">
      {segments.map((seg, i) => (
        <div key={`${seg.label}-${i}`} className="flex items-center gap-2">
          {leadingSeparator || i > 0 ? (
            <ChevronRight className="h-3.5 w-3.5 text-subtle" />
          ) : null}
          {seg.href ? (
            <RouterLink
              to={seg.href}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {seg.label}
            </RouterLink>
          ) : (
            <span className="text-foreground">{seg.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

export function BreadcrumbSetter({ segments }: { segments: BreadcrumbSegment[] }) {
  const { setSegments } = useContext(BreadcrumbContext);
  const serialized = JSON.stringify(segments);
  useEffect(() => {
    setSegments(segments);
    // `segments` is re-created each render; `serialized` gates the effect on its content.
  }, [serialized, setSegments]);

  return null;
}
